import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Eye, Megaphone, X } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { AUDIENCE_LABELS, fetchUpcomingAnnouncements, type Audience } from "@/lib/announcements";

export const Route = createFileRoute("/_authenticated/super-admin/announcement-preview")({
  head: () => ({
    meta: [
      { title: "Announcement preview · Landoo Super Admin" },
      {
        name: "description",
        content:
          "Preview scheduled Landoo announcements exactly as each audience will see them before their start date.",
      },
      { property: "og:title", content: "Announcement preview · Landoo Super Admin" },
      {
        property: "og:description",
        content: "See how upcoming announcements will look to customers, partners and admins.",
      },
    ],
  }),
  component: PreviewPage,
});

const AUDIENCES: Audience[] = ["all", "customers", "partners", "admins"];

function PreviewPage() {
  const [audience, setAudience] = useState<Audience>("all");
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["announcements", "upcoming"],
    queryFn: fetchUpcomingAnnouncements,
  });

  const shown = rows.filter(
    (a) => a.audience === audience || (audience !== "all" && a.audience === "all"),
  );

  return (
    <DashboardShell title="Announcement preview" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <section className="rounded-xl border border-border bg-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Eye className="h-4 w-4 text-primary" /> Scheduled announcements
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          These have not started yet. Pick an audience to see exactly how the banner will appear to
          them.
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          {AUDIENCES.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setAudience(a)}
              className={`rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                audience === a
                  ? "bg-primary text-primary-foreground"
                  : "border border-border bg-background text-muted-foreground hover:bg-accent"
              }`}
            >
              {AUDIENCE_LABELS[a]}
            </button>
          ))}
        </div>
      </section>

      <div className="mt-4 rounded-xl border border-dashed border-border bg-muted/30 p-4">
        <p className="mb-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Banner preview · {AUDIENCE_LABELS[audience]}
        </p>
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No upcoming announcements for this audience.
          </p>
        ) : (
          <div className="flex flex-col items-center gap-2">
            {shown.map((a) => (
              <div key={a.id} className="w-full max-w-3xl">
                <div className="rounded-xl border border-border bg-card/95 p-3 shadow-lg">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 rounded-lg bg-primary/10 p-1.5 text-primary">
                      <Megaphone className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground">{a.title}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                        {a.message}
                      </p>
                    </div>
                    <span className="rounded-md p-1 text-muted-foreground">
                      <X className="h-4 w-4" />
                    </span>
                  </div>
                </div>
                <p className="mt-1 px-1 text-[11px] text-muted-foreground">
                  Goes live {new Date(a.start_date).toLocaleString()}
                  {a.end_date ? ` · ends ${new Date(a.end_date).toLocaleString()}` : ""}
                  {a.active ? "" : " · currently inactive, will not show until activated"}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
